import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, CardContent, Typography, Table, TableBody, TableCell, TableHead,
  TableRow, Chip, CircularProgress, Alert, Box, Stack, IconButton, Tooltip
} from '@mui/material';
import { Refresh as RefreshIcon, PrecisionManufacturing as FactoryIcon } from '@mui/icons-material';
import { getComponentReadiness } from '../../services/procurementService';

/**
 * Component readiness for a MANUFACTURE purchase order: per production-BOM
 * component — required (in the component's stock unit), available at the
 * manufacturer warehouse (stock-levels engine), on order to that warehouse,
 * and the shortfall. Renders one table per PO line (assembly).
 */
export default function ComponentReadinessPanel({ poId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!poId) return;
    setLoading(true);
    setError(null);
    try {
      setData(await getComponentReadiness(poId));
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Failed to load component readiness');
    } finally {
      setLoading(false);
    }
  }, [poId]);

  useEffect(() => { load(); }, [load]);

  const fmtQty = (v, uom) => {
    if (v == null) return '—';
    const n = Number(v);
    const s = Number.isInteger(n) ? String(n) : n.toFixed(2);
    return uom && uom !== 'units' ? `${s} ${uom}` : s;
  };

  // Money in the component's OWN currency — never hardcode $ (components can be
  // priced in AUD/GBP/EUR on their inbound POs). Sub-unit costs need 4dp
  // (labels/caps run 0.14xx); totals 2dp.
  const CCY_SYMBOL = { USD: '$', GBP: '£', EUR: '€', AUD: 'A$', CHF: 'CHF ', SGD: 'S$', CAD: 'C$' };
  const fmtCost = (v, ccy) => {
    if (v == null) return '—';
    const n = Number(v);
    const c = String(ccy || 'USD').toUpperCase();
    const sym = CCY_SYMBOL[c] || `${c} `;
    return `${sym}${n.toFixed(Math.abs(n) < 1 ? 4 : 2)}`;
  };

  // Where the unit cost came from — shown as a caption so an unexpected number
  // is explainable (established at the manufacturer vs expected from an inbound PO).
  const costSourceLabel = (s) => ({
    warehouse: 'at manufacturer',
    on_order: 'expected (on order)',
    standard: 'standard',
    mixed_currency: 'mixed currencies',
  }[s] || '');

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <FactoryIcon color="primary" fontSize="small" />
          <Typography variant="subtitle1" fontWeight={700}>Component readiness</Typography>
          {data?.warehouse_code && (
            <Chip size="small" variant="outlined" label={`at ${data.warehouse_code}`} />
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Tooltip title="Refresh">
            <span><IconButton size="small" onClick={load} disabled={loading}><RefreshIcon fontSize="small" /></IconButton></span>
          </Tooltip>
        </Stack>

        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
        {loading && !data && <Box sx={{ textAlign: 'center', py: 2 }}><CircularProgress size={24} /></Box>}

        {(data?.lines || []).map((line) => (
          <Box key={line.po_item_id} sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <b style={{ fontFamily: 'monospace' }}>{line.assembly_sku}</b>
              {' — ordered '}{line.ordered_quantity}
              {line.received_quantity > 0 ? `, produced so far ${line.received_quantity}` : ''}
              {line.remaining_to_produce != null ? `, remaining ${line.remaining_to_produce}` : ''}
              {line.line_sku && line.line_sku !== line.assembly_sku && (
                <Typography component="span" variant="caption" color="text.secondary">
                  {' '}(built from bulk-fill PO line {line.line_sku})
                </Typography>
              )}
            </Typography>
            {line.error ? (
              <Alert severity="warning">{line.error}</Alert>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Component</TableCell>
                    <TableCell align="right">Per unit</TableCell>
                    <TableCell align="right">Unit cost</TableCell>
                    <TableCell align="right">Required (remaining)</TableCell>
                    <TableCell align="right">Est. cost</TableCell>
                    <TableCell align="right">Available</TableCell>
                    <TableCell align="right">On order</TableCell>
                    <TableCell align="right">Shortfall</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {line.components.map((c) => (
                    <TableRow key={c.component_sku}>
                      <TableCell>
                        <Typography variant="body2">{c.product_name || '—'}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                          {c.component_sku}
                        </Typography>
                        {c.uom_error && (
                          <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                            {c.uom_error}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {fmtQty(c.bom_quantity, c.bom_unit)}
                        {c.bom_unit !== c.stock_uom && c.required_per_unit_stock != null && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            = {fmtQty(c.required_per_unit_stock, c.stock_uom)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {c.unit_cost_source === 'mixed_currency' ? (
                          <Tooltip title="Open component POs are in more than one currency — can't show a single expected cost" arrow>
                            <Typography variant="caption" color="warning.main" sx={{ cursor: 'help' }}>mixed ccy</Typography>
                          </Tooltip>
                        ) : fmtCost(c.unit_cost, c.unit_cost_currency)}
                        {c.unit_cost != null && c.unit_cost_source !== 'mixed_currency' && costSourceLabel(c.unit_cost_source) && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {costSourceLabel(c.unit_cost_source)}
                          </Typography>
                        )}
                        {/* No cost at this warehouse — the receive/backflush
                            will NOT stay blank: it borrows the highest non-zero
                            cost from another warehouse. Surface that so a blank
                            here can't silently diverge from what a receipt
                            books (EF-59692 booked 0.77 from G3PL while this
                            panel showed '—'). */}
                        {c.unit_cost == null && c.fallback_cost > 0 && (
                          <Tooltip
                            arrow
                            title={c.fallback_cost_source === 'transfer_in'
                              ? `No cost is set for this component at ${data?.warehouse_code || 'this warehouse'}. If received now, the backflush will use ${fmtCost(c.fallback_cost, c.fallback_cost_currency)} — the latest transfer-in cost (shipped from ${c.fallback_cost_warehouse}). Set a landed cost at ${data?.warehouse_code || 'this warehouse'} in Products & COGS to include freight/customs.`
                              : `No cost is set for this component at ${data?.warehouse_code || 'this warehouse'}. If received now, the backflush will borrow ${fmtCost(c.fallback_cost, c.fallback_cost_currency)} from ${c.fallback_cost_warehouse} — another warehouse's cost, possibly wrong here. Set a cost at ${data?.warehouse_code || 'this warehouse'} in Products & COGS to fix this.`}
                          >
                            {/* tabIndex so MUI's focus trigger shows the tooltip
                                for keyboard users too, not just on hover. */}
                            <Typography variant="caption" color="warning.main" tabIndex={0} sx={{ display: 'block', cursor: 'help' }}>
                              {c.fallback_cost_source === 'transfer_in'
                                ? <>will use {fmtCost(c.fallback_cost, c.fallback_cost_currency)} (transfer-in from {c.fallback_cost_warehouse})</>
                                : <>will borrow {fmtCost(c.fallback_cost, c.fallback_cost_currency)} ({c.fallback_cost_warehouse})</>}
                            </Typography>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell align="right">{fmtQty(c.required_remaining_stock, c.stock_uom)}</TableCell>
                      <TableCell align="right">{fmtCost(c.extended_cost, c.unit_cost_currency)}</TableCell>
                      <TableCell align="right">{fmtQty(c.available_at_warehouse, c.stock_uom)}</TableCell>
                      <TableCell align="right">{fmtQty(c.on_order_to_warehouse, c.stock_uom)}</TableCell>
                      <TableCell align="right">
                        {c.shortfall == null ? '—' : c.shortfall > 0 ? (
                          <Chip size="small" color="error" label={fmtQty(c.shortfall, c.stock_uom)} />
                        ) : (
                          <Chip size="small" color="success" label="ready" variant="outlined" />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Box>
        ))}
      </CardContent>
    </Card>
  );
}
